<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store.js';
import { useStore } from 'vuex';
import Icon from 'next/icon/Icon.vue';
import { isAConversationRoute } from 'dashboard/helper/routeHelpers';
import UnreadCountBadge from 'dashboard/components-next/Inbox/UnreadCountBadge.vue';
import InboxUnreadTooltip from 'dashboard/components-next/Inbox/InboxUnreadTooltip.vue';
import { useInboxSidebarBadge } from 'dashboard/composables/useInboxSidebarBadge';

const props = defineProps({
  name: { type: String, default: '' },
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  mobileActiveAccent: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
  description: { type: String, default: '' },
});

const emit = defineEmits(['toggle']);
const route = useRoute();
const router = useRouter();
const store = useStore();
const isInboxTooltipVisible = ref(false);
const triggerRef = ref(null);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const conversationStats = useMapGetter('conversationStats/getStats');
const unreadConversationCount = computed(() => {
  if (props.name !== 'Conversation') return 0;

  const unreadCounts = conversationStats.value?.aceleresBoxUnreadCounts || {};
  return ['novos', 'meus', 'outros', 'pessoais'].reduce(
    (total, box) => total + Number(unreadCounts[box] || 0),
    0
  );
});
// Aceleres: a Caixa de Entrada soma as notificacoes e os e-mails nao lidos.
const { totalUnreadCount, tooltipTitle, tooltipLines, ariaLabel, fetchCounts } =
  useInboxSidebarBadge(props.name === 'Inbox');
const badgeValue = computed(() => {
  if (props.name === 'Conversation') return unreadConversationCount.value;
  if (props.name === 'Inbox') return totalUnreadCount.value;

  return Number(dynamicCount.value || 0);
});
const count = computed(() =>
  badgeValue.value > 99 ? '99+' : badgeValue.value
);

const shouldFetchConversationStats = computed(
  () => props.name === 'Conversation' && !isAConversationRoute(route.name, true)
);

const fetchConversationStats = () => {
  if (shouldFetchConversationStats.value) {
    store.dispatch('conversationStats/get', { immediate: true });
  }
};

watch(shouldFetchConversationStats, fetchConversationStats, {
  immediate: true,
});

onMounted(() => {
  if (props.name === 'Inbox') fetchCounts();
});

const handleClick = event => {
  emit('toggle');

  if (
    !props.to ||
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  event.preventDefault();
  router.push(props.to).catch(() => {});
};
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    ref="triggerRef"
    class="group relative flex h-8 min-w-0 items-center gap-2 rounded-lg px-1.5 py-1"
    role="button"
    draggable="false"
    :to="to"
    :title="name === 'Inbox' ? undefined : label"
    :aria-label="name === 'Inbox' ? ariaLabel : undefined"
    :aria-describedby="
      name === 'Inbox' ? 'inbox-unread-tooltip-expanded' : undefined
    "
    @keydown.esc="isInboxTooltipVisible = false"
    :class="{
      'text-n-slate-12 bg-n-alpha-2 font-medium': isActive && !hasActiveChild,
      'text-n-slate-12 font-medium': hasActiveChild,
      'max-md:bg-n-ruby-3 max-md:!text-n-ruby-11':
        mobileActiveAccent && hasActiveChild,
      'text-n-slate-11 hover:bg-n-alpha-2': !isActive && !hasActiveChild,
    }"
    @mouseenter="isInboxTooltipVisible = name === 'Inbox'"
    @mouseleave="isInboxTooltipVisible = false"
    @focusin="isInboxTooltipVisible = name === 'Inbox'"
    @focusout="isInboxTooltipVisible = false"
    @click.stop="handleClick"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="size-2 -top-px ltr:-right-px rtl:-left-px bg-n-brand absolute rounded-full border border-n-solid-2"
      />
    </div>
    <div class="flex items-center gap-1.5 flex-grow min-w-0 flex-1">
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <InboxUnreadTooltip
        v-if="name === 'Inbox'"
        id="inbox-unread-tooltip-expanded"
        :anchor="triggerRef"
        placement="below"
        :title="tooltipTitle"
        :lines="tooltipLines"
        :visible="isInboxTooltipVisible"
      />
      <UnreadCountBadge v-if="name === 'Inbox'" :count="badgeValue" />
      <span
        v-else-if="badgeValue"
        class="size-5 min-w-5 rounded-full bg-n-ruby-9 px-1 flex-shrink-0 grid place-items-center text-[10px] leading-none font-semibold text-n-ruby-3"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="description"
      class="i-lucide-info size-3 flex-shrink-0 opacity-0 group-hover:opacity-60 transition-opacity cursor-default"
      :title="description"
      @click.stop
    />
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
